<template>
  <div>
    <HgListingCard
      :address="formattedAddress"
      :agencyLogoUrl="listerLogo"
      :description="description"
      :hasVirtualTour="hasVirtualTour"
      :href="detailPageRoute"
      :imageUrl="imageUrl"
      :isFavourite="true"
      :isPremium="isPremiumListing"
      :isTenantPlus="isTenantPlusListing"
      :isTop="isTopListing"
      :lazyLoadImages="lazyLoadImages"
      :listingCardSize="listingCardSize"
      :listingImages="listingImages"
      :listingWithFloorSpace="listingWithFloorSpace"
      :livingSpace="livingSpace"
      :numberOfRooms="numberOfRooms"
      :price="formattedPrice"
      :priceArea="area"
      :isFavouriteButtonLoading="isFavouriteButtonLoading"
      :priceInterval="interval"
      :theme="theme"
      :title="imageFallbackTitle"
      @click="emit('item-clicked', { listingId })"
      @favouritesClick="removeFavourite()"
      @sliderClick="emit('slider-clicked')"
    >
      <template #favoriteNoteFooter>
        <FavoriteNote
          :listingId="listingId"
          :detailPageRoute="detailPageRoute"
          :listingPosition="listingPosition"
        />
      </template>
    </HgListingCard>
  </div>
</template>

<script lang="ts" setup>
import { removeNote } from '@/components/Note/Note';
import FavoriteNote from '@/components/Note/NoteFooter/NoteFooter.vue';
import { getCurrentPlatform } from '@/helpers/envService';
import {
  InteractionContainerName,
  InteractionEvent,
  InteractionGoal,
  trackEvent,
} from '@/helpers/GTM/GTMHelper';
import {
  getBuyPrice,
  getListingAddress,
  getListingImage,
  getListingImages,
  getListingVirtualTour,
  getNumberOfImagesFromListing,
  getRentGross,
  isRemoteViewingAvailable,
} from '@/helpers/ListingData/ListingData';
import { HgListingCard, type HgListingCardImage, HgListingCardSize } from '@smg-real-estate/domus';
import { AreaThePriceRefersTo, OfferType, PriceInterval } from '@smg-real-estate/hg-rets-schema';
import {
  getListingSpace,
  isListingWithFloorSpace,
  ListingCardSize,
  type ListingItem,
  ListingType,
} from '@smg-real-estate/hg-search-libs';
import { computed, onMounted, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useStore } from 'vuex';

const props = defineProps({
  listingItem: {
    type: Object as () => ListingItem,
    required: true,
  },
  listingVisited: {
    type: Boolean,
    required: true,
  },
  lazyLoadImages: {
    type: Boolean,
    default: true,
  },
  listingPosition: {
    type: Number,
    required: true,
  },
});

const emit = defineEmits(['item-clicked', 'slider-clicked']);

const store = useStore();
const { t, locale } = useI18n();

const hgRetsListing = ref(props.listingItem.listing);
const listingType = ref(props.listingItem.listingType);
const listerBranding = ref(props.listingItem.listerBranding as any);
const isComponentMounted = ref(false);

const detailPageRouteMap = {
  de: {
    [OfferType.Rent]: 'mieten',
    [OfferType.Buy]: 'kaufen',
  },
  fr: {
    [OfferType.Rent]: 'louer',
    [OfferType.Buy]: 'acheter',
  },
  en: {
    [OfferType.Rent]: 'rent',
    [OfferType.Buy]: 'buy',
  },
  it: {
    [OfferType.Rent]: 'affittare',
    [OfferType.Buy]: 'acquistare',
  },
};

const detailPageRoute = computed(() => {
  if (hgRetsListing.value.platforms && hgRetsListing.value.platforms[0] === 'immoscout24') {
    const savedFavorites = store.state.favourites.favouriteListingDescriptors;
    if (
      savedFavorites.find(
        (fav) => fav.listingId === hgRetsListing.value.id && fav.platform === 'is24',
      )
    ) {
      return `${import.meta.env.VUE_APP_BASE_URL}/${hgRetsListing.value.id}`;
    }
  }

  return `${import.meta.env.VUE_APP_BASE_URL}/${
    detailPageRouteMap[locale.value][hgRetsListing.value.offerType]
  }/${hgRetsListing.value.id}`;
});

const formattedAddress = computed(() => getListingAddress(hgRetsListing.value) || '');

const numberOfRooms = computed(() => {
  if (!isListingWithFloorSpace(hgRetsListing.value)) {
    return hgRetsListing.value.characteristics?.numberOfRooms;
  }

  if (!livingSpace.value) {
    return hgRetsListing.value.characteristics?.numberOfRooms;
  }

  return undefined;
});

const description = computed(() =>
  hgRetsListing.value.localization?.de?.text?.description
    ?.split('<p>')
    .join('')
    .split('</p>')
    .join(' '),
);

const listingWithFloorSpace = computed(() => isListingWithFloorSpace(hgRetsListing.value));

const livingSpace = computed(() => getListingSpace(hgRetsListing.value));

const offerType = computed(() => hgRetsListing.value.offerType);

const prices = computed(() => hgRetsListing.value.prices);

const listingId = computed(() => hgRetsListing.value.id);

const imageFallbackTitle = computed(() => hgRetsListing.value.localization?.de?.text?.title);

const hasVirtualTour = computed(
  () => !!getListingVirtualTour(hgRetsListing.value.localization?.de),
);

const listerLogo = computed(() => listerBranding.value?.logoUrl);

const isFavouriteLoading = computed(
  () => !isComponentMounted.value || store.state.favourites.isFavouritesEnabled,
);

const isFavouriteButtonLoading = computed(
  () => store.getters.isFavouriteLoading?.(listingId.value) || false,
);

const hasRemoteViewing = computed(() => isRemoteViewingAvailable(props.listingItem.listing));

const isPremiumListing = computed(
  () => props.listingItem?.listingType?.type === ListingType.PREMIUM,
);

const isTenantPlusListing = computed(
  () => props.listingItem?.listing.valueAddedServices?.isTenantPlusListing,
);

const isTopListing = computed(() => props.listingItem?.listingType?.type === ListingType.TOP);

const listingCardSize = computed(
  () => HgListingCardSize[props.listingItem?.listingCard?.size || ListingCardSize.LARGE],
);

const theme = computed(() => getCurrentPlatform());

const showUnit = computed(() => {
  return (
    (offerType.value === OfferType.Rent && interval.value === PriceInterval.Month) ||
    (offerType.value === OfferType.Rent && interval.value === PriceInterval.Year) ||
    (offerType.value === OfferType.Buy && area.value === AreaThePriceRefersTo.All)
  );
});

const showInterval = computed(() => {
  if (offerType.value === OfferType.Buy) {
    return undefined;
  }

  if (!!interval.value && !!area.value) {
    return t(`priceInterval.${interval.value}`) as string;
  }

  return undefined;
});

const area = computed(() => {
  if (offerType.value === OfferType.Rent && prices.value?.rent) {
    return {
      [AreaThePriceRefersTo.All]: undefined,
      [AreaThePriceRefersTo.M2]: 'm',
      [AreaThePriceRefersTo.Km2]: 'km',
    }[prices.value?.rent.area];
  }

  return undefined;
});

const interval = computed(() => {
  if (offerType.value === OfferType.Rent && prices.value?.rent) {
    return prices.value?.rent.interval;
  }

  if (offerType.value === OfferType.Buy && prices.value?.buy) {
    // @ts-expect-error interval does not exist on type Buy
    return prices.value?.buy.interval;
  }

  return undefined;
});

const price = computed(() => {
  switch (offerType.value) {
    case OfferType.Rent:
      return getRentGross(prices.value, locale.value);
    case OfferType.Buy:
      return getBuyPrice(prices.value, locale.value);
    default:
      return undefined;
  }
});

const currency = computed(() => prices.value?.currency);

const formattedPrice = computed(() =>
  price.value ? `${currency.value} ${price.value}` : undefined,
);

const imageUrl = computed(() => {
  const res = listingImage(hgRetsListing.value, 0);
  return res ? res.url : '';
});

const listingImages = computed(() => getListingImages(hgRetsListing.value) as HgListingCardImage[]);

onMounted(() => {
  isComponentMounted.value = true;
});

function listingImage(listing, index) {
  return getListingImage(listing, index);
}

function getNumberOfImages(listing) {
  return getNumberOfImagesFromListing(listing);
}

function removeFavourite() {
  store.dispatch('removeFavourite', listingId.value);
  removeNote(listingId.value);
  trackEvent(InteractionEvent.FAVOURITES_REMOVE, {
    h_event_category_006: InteractionContainerName.FAVOURITES_REMOVE,
    h_event_action_007: InteractionGoal.REMOVE_FROM_FAVOURITES,
    h_event_label_008: InteractionContainerName.FAVOURITES_LIST,
    h_listing_advertisement_id_120: listingId.value,
  });
}
</script>

<style lang="scss" module>
@import '../../assets/variables';
@import 'listItem.mixin';

@include list-item;

.extraButtons {
  display: flex;
  align-items: center;
}

@media (min-width: $viewport-medium-min) {
  .itemLink {
    .description {
      min-height: 3.5rem;
    }
  }
}
</style>
